import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CustomColorPicker } from './PropertiesPanel';
import type { EditorialPropertyContext } from './editorialBlockProperties';
import { ImageSourceField } from './editorialBlockProperties';
import { resolveHeaderNavColor } from '../utils/headerResponsiveCss';

const LAYOUT_LABELS: Record<string, string> = {
  'modern-clean': 'Modern Clean',
  'bold-centered': 'Bold Centered',
  action: 'Action Area',
  'dark-accent': 'Dark Accent',
};

function HeaderColorField({
  ctx,
  id,
  label,
  value,
  onChange,
}: {
  ctx: EditorialPropertyContext;
  id: string;
  label: string;
  value: string;
  onChange: (color: string) => void;
}) {
  const {
    showColorPicker,
    setShowColorPicker,
    colorPickerRef,
    getEmailBackgroundColors,
    brandColors,
    showAdvancedPicker,
    setShowAdvancedPicker,
    setAdvancedPickerPosition,
  } = ctx;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowColorPicker(showColorPicker === id ? null : id)}
          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl flex items-center gap-3 bg-white hover:border-slate-300 transition-colors"
        >
          <div className="w-8 h-8 rounded-lg border border-slate-200 shadow-inner" style={{ backgroundColor: value }} />
          <span className="text-sm text-slate-700 font-mono uppercase">{value}</span>
        </button>
        {showColorPicker === id && (
          <div ref={colorPickerRef} className="absolute z-[101] mt-1 left-0 right-0">
            <CustomColorPicker
              currentColor={value}
              onChange={onChange}
              pickerType="solid"
              emailColors={getEmailBackgroundColors()}
              brandColors={brandColors}
              showAdvancedPicker={showAdvancedPicker}
              setShowAdvancedPicker={setShowAdvancedPicker}
              setAdvancedPickerPosition={setAdvancedPickerPosition}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function HeaderPropertiesPanel({ ctx }: { ctx: EditorialPropertyContext }) {
  const { block, handleContentChange } = ctx;
  const layout = block.content.layout || 'modern-clean';
  const links: { label?: string; url?: string }[] = Array.isArray(block.content.links) ? block.content.links : [];
  const bg = block.styles?.backgroundColor;
  const resolvedNavColor = resolveHeaderNavColor(bg, block.content.navTextColor, layout);
  const logoWidthPx = parseInt(String(block.content.logoMaxWidth || '120').replace('px', ''), 10);
  const safeLogoWidth = Number.isFinite(logoWidthPx) ? logoWidthPx : 120;

  const updateLink = (index: number, patch: Partial<{ label: string; url: string }>) => {
    const next = links.map((link, i) => (i === index ? { ...link, ...patch } : link));
    handleContentChange('links', next);
  };

  const removeLink = (index: number) => {
    handleContentChange(
      'links',
      links.filter((_, i) => i !== index)
    );
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Layout preset</p>
        <p className="text-sm font-medium text-slate-800">{LAYOUT_LABELS[layout] || layout}</p>
      </div>

      <div className="space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Logo</h4>
        <ImageSourceField ctx={ctx} field="logoUrl" label="Logo image" />
        {block.content.logoUrl && (
          <div className="p-3 border border-slate-200 rounded-xl bg-slate-50/50 flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 p-1 flex items-center justify-center overflow-hidden">
              <img src={block.content.logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Active logo</p>
              <p className="text-xs text-slate-700 truncate">{block.content.imageName || 'brand-logo.png'}</p>
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1 flex justify-between">
            <span>Max width</span>
            <span className="text-slate-500 font-normal">{safeLogoWidth}px</span>
          </label>
          <input
            type="range"
            min={50}
            max={300}
            value={safeLogoWidth}
            onChange={(e) => handleContentChange('logoMaxWidth', `${e.target.value}px`)}
            className="v2-slider w-full h-1"
            style={{
              background: `linear-gradient(90deg, #0F172A ${((safeLogoWidth - 50) / 250) * 100}%, #E2E8F0 ${((safeLogoWidth - 50) / 250) * 100}%)`,
            }}
          />
        </div>
      </div>

      <div className="space-y-4 pt-2 border-t border-slate-200">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Navigation</h4>
        <div className="space-y-3">
          {links.map((link, idx) => (
            <div
              key={idx}
              className="p-3 border border-slate-200 rounded-lg bg-slate-50 space-y-2 relative group hover:border-slate-300 transition-all"
            >
              <button
                type="button"
                onClick={() => removeLink(idx)}
                className="absolute top-2 right-2 text-slate-400 hover:text-red-500 hover:bg-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                title="Remove link"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
              <div>
                <label className="block text-[11px] font-semibold tracking-wide text-slate-500 mb-1 uppercase">Label</label>
                <input
                  type="text"
                  value={link.label ?? ''}
                  onChange={(e) => updateLink(idx, { label: e.target.value })}
                  className="w-[calc(100%-24px)] px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:border-slate-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-wide text-slate-500 mb-1 uppercase">URL</label>
                <input
                  type="text"
                  value={link.url ?? ''}
                  onChange={(e) => updateLink(idx, { url: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:border-slate-400 outline-none"
                />
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => handleContentChange('links', [...links, { label: 'New Link', url: '#' }])}
          className="w-full py-2.5 bg-white border-2 border-dashed border-slate-300 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-2"
        >
          <PlusIcon className="w-4 h-4" /> Add link
        </button>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Link weight</label>
            <select
              value={block.content.navFontWeight || '600'}
              onChange={(e) => handleContentChange('navFontWeight', e.target.value)}
              className="w-full px-2 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
            >
              <option value="400">Regular</option>
              <option value="500">Medium</option>
              <option value="600">Semibold</option>
              <option value="700">Bold</option>
            </select>
          </div>
        </div>

        <HeaderColorField
          ctx={ctx}
          id="header-nav-color"
          label="Link color"
          value={block.content.navTextColor || resolvedNavColor}
          onChange={(color) => handleContentChange('navTextColor', color)}
        />

        {layout === 'dark-accent' && (
          <HeaderColorField
            ctx={ctx}
            id="header-accent-color"
            label="Accent link color (first link)"
            value={block.content.accentLinkColor || '#CA8A04'}
            onChange={(color) => handleContentChange('accentLinkColor', color)}
          />
        )}
      </div>

      {layout === 'action' && (
        <div className="space-y-4 pt-2 border-t border-slate-200">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Action button</h4>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Button text</label>
            <input
              type="text"
              value={block.content.actionText || ''}
              onChange={(e) => handleContentChange('actionText', e.target.value)}
              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-slate-400 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Button URL</label>
            <input
              type="text"
              value={block.content.actionUrl || ''}
              onChange={(e) => handleContentChange('actionUrl', e.target.value)}
              placeholder="https://"
              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-slate-400 outline-none"
            />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <HeaderColorField
              ctx={ctx}
              id="header-action-bg"
              label="Button background"
              value={block.content.actionButtonBg || '#FFFFFF'}
              onChange={(color) => handleContentChange('actionButtonBg', color)}
            />
            <HeaderColorField
              ctx={ctx}
              id="header-action-text"
              label="Button text color"
              value={block.content.actionButtonColor || '#1E293B'}
              onChange={(color) => handleContentChange('actionButtonColor', color)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
